/**
 * AUSTRALIAN TAX RULES — Financial Year 2025-26
 *
 * Versioned data file for all year-specific tax constants. Refactored out
 * of `calculations.ts` per BACKLOG B30 / B116. Pattern mirrors
 * `land-tax-rules.ts` (the precedent established in ARCHITECTURE §18).
 *
 * ANNUAL UPDATE PROCEDURE (every June, before 1 July):
 *   1. Copy this file to `tax-rules-{NEXT-FY}.ts` (e.g. `tax-rules-2026-27.ts`).
 *   2. Update each table from the current ATO pages (links in `sourceUrls`).
 *   3. In `calculations.ts`, swap the `CURRENT_TAX_RULES` import to the new file.
 *   4. Run `npm run test:unit` — bracket-edge tests should still pass.
 *   5. Run `npm run build` and verify no implicit hardcoded values remain.
 *   6. Update ARCHITECTURE.md §18 review date.
 *
 * The "Stage 3" tax cuts apply from FY 2024-25 onwards. Brackets here are
 * the post-Stage-3 schedule (16% / 30% / 37% / 45% with $135k / $190k cuts).
 */

export const TAX_RULES_2025_26 = {
  fyLabel: "2025-26",
  sourceUrls: [
    "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents",
    "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates",
    "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-reduction/medicare-levy-reduction-for-low-income-earners",
    "https://www.ato.gov.au/individuals-and-families/study-and-training-support-loans/loan-repayment/study-and-training-loan-repayment-thresholds-and-rates",
  ],

  /**
   * Resident income tax brackets — Stage 3 schedule. Each entry is the upper
   * threshold of that bracket, the marginal rate, and `baseAtLower` (the
   * cumulative dollar tax owed when income equals the LOWER bound of the
   * bracket — i.e. the tax owed at the previous bracket's upper threshold).
   */
  incomeTax: {
    brackets: [
      { upper: 18200,    rate: 0,    baseAtLower: 0 },
      { upper: 45000,    rate: 0.16, baseAtLower: 0 },
      { upper: 135000,   rate: 0.30, baseAtLower: 4288 },
      { upper: 190000,   rate: 0.37, baseAtLower: 31288 },
      { upper: Infinity, rate: 0.45, baseAtLower: 51638 },
    ],
  },

  /**
   * Low Income Tax Offset (LITO). Max $700, phases out 5c per $1 from $37,500
   * to $45,000 ($325 at $45k), then 1.5c per $1 to $66,667 (zero).
   */
  lito: {
    max: 700,
    phaseOutLowerThreshold: 37500,
    phaseOutLowerRate: 0.05,
    midPointAmount: 325,
    phaseOutUpperThreshold: 45000,
    phaseOutUpperRate: 0.015,
    upperBoundary: 66667,
  },

  /**
   * Medicare levy (singles). 2% standard rate above the upper threshold,
   * phase-in at 10% of excess over the lower threshold. The half-levy
   * `reducedRate` applies when the user qualifies for partial Medicare
   * exemption (the `medicareReduction` flag).
   *
   * NOTE: family Medicare-levy thresholds are higher and depend on
   * dependants. Out of B116 V1 scope; flag as future work if required.
   */
  medicareLevy: {
    rate: 0.02,
    reducedRate: 0.01,
    singleLowerThreshold: 27222,
    singleUpperThreshold: 34027,
    phaseInRate: 0.10,
  },

  /**
   * Medicare Levy Surcharge (MLS). Bracket-stepped over income thresholds.
   * Family path applies when the lens has a partner (combined household
   * income evaluated against family thresholds, with $1,500 uplift per
   * dependent child after the first).
   */
  mls: {
    singles: [
      { upper: 101000,   rate: 0      },
      { upper: 118000,   rate: 0.01   },
      { upper: 158000,   rate: 0.0125 },
      { upper: Infinity, rate: 0.015  },
    ],
    family: [
      { upper: 202000,   rate: 0      },
      { upper: 236000,   rate: 0.01   },
      { upper: 316000,   rate: 0.0125 },
      { upper: Infinity, rate: 0.015  },
    ],
    dependentChildUplift: 1500,
  },

  /**
   * HELP / HECS compulsory repayment 2025-26. **System changed in 2025-26**:
   * the previous 18-tier "single rate × full income" schedule was replaced
   * by a 3-tier marginal system. Each tier specifies the income range, a
   * fixed dollar amount owed at the lower bound, and a marginal rate
   * applied to income ABOVE the lower bound. The top tier reverts to a
   * flat-rate × full income (legacy behaviour preserved at the high end).
   *
   * ANNUAL REVIEW WARNING: HELP changed system SHAPE (not just thresholds)
   * in 2025-26. Future annual reviews must re-read the ATO page from
   * scratch — diff-the-numbers is not sufficient when the schedule
   * structure can change. See LESSONS L43.
   *
   * Source: https://www.ato.gov.au/tax-rates-and-codes/study-and-training-support-loans-rates-and-repayment-thresholds
   */
  help: {
    minIncomeThreshold: 67000,
    tiers: [
      { lower: 67000,    upper: 125000,   fixedAtLower: 0,    marginalRate: 0.15, fullIncomeRate: null as number | null },
      { lower: 125000,   upper: 179285,   fixedAtLower: 8700, marginalRate: 0.17, fullIncomeRate: null as number | null },
      { lower: 179286,   upper: Infinity, fixedAtLower: 0,    marginalRate: 0,    fullIncomeRate: 0.10 as number | null },
    ],
  },
} as const;

export type TaxRules = typeof TAX_RULES_2025_26;

/**
 * Module-level alias re-exported so consumers don't hardcode the year. Swap
 * this to the next FY's file when rolling over (June each year).
 *
 * **B130 Wave 1 (Day 56):** retained as the BACKWARDS-COMPATIBILITY SHIM
 * for `calculations.ts` which currently reads `RULES.incomeTax.brackets`,
 * `RULES.lito.max`, `RULES.medicareLevy.rate`, etc. directly. Wave 6 deletes
 * this export and swaps every helper to read via `getTaxRule()` from
 * `tax-rules-loader.ts`. The new `RULES_2025_26: TaxRule[]` below is the
 * canonical schema-conformant form; numeric values mirror this object
 * verbatim. Zero behaviour change in Wave 1.
 */
export const CURRENT_TAX_RULES = TAX_RULES_2025_26;

// ─── B130 Wave 1: schema-conformant rules array + registry call ──────────────

import { registerFY } from "./tax-rules-loader";
import type { TaxRule } from "./tax-rules-types";

/**
 * Federal tax rules for FY 2025-26 in the canonical B130 Wave 0 schema.
 * Numeric values are bit-identical mirrors of `TAX_RULES_2025_26` above.
 *
 * Wave 6 swap: `calculations.ts` will read these via the loader; the
 * `TAX_RULES_2025_26` shim above will be deleted.
 */
export const RULES_2025_26: TaxRule[] = [
  // ── Income tax — Stage 3 brackets ─────────────────────────────────────────
  {
    rule_id: "federal/income_tax/2025-26",
    fy: "2025-26",
    category: "income_tax",
    jurisdiction: "federal",
    shape: "brackets",
    plain_english:
      "Federal income tax for resident individuals — Stage 3 schedule. " +
      "Tax-free up to $18,200; 16% on $18,201–$45,000; 30% on $45,001–$135,000; " +
      "37% on $135,001–$190,000; 45% above $190,000.",
    technical:
      "ITAA 1997 Division 4. Stage 3 brackets effective from FY 2024-25 onwards; " +
      "boundaries $135k / $190k (replaced pre-Stage-3 $120k / $180k). " +
      "fixed_at_lower captures cumulative tax owed at each bracket's lower bound.",
    legislation_ref: "ITAA 1997 Division 4",
    source_url: "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents",
    source_retrieved: "2026-05-02",
    last_reviewed: "2026-05-04",
    applies_to: ["resident individuals"],
    excluded: ["non-residents (separate schedule)", "working holiday makers"],
    formula_ref: "baseTax / calculateMarginalRate (calculations.ts)",
    brackets: [
      { lower: 0,      upper: 18200,  lower_inclusive: true,  upper_inclusive: true, rate: 0,    fixed_at_lower: 0 },
      { lower: 18200,  upper: 45000,  lower_inclusive: false, upper_inclusive: true, rate: 0.16, fixed_at_lower: 0 },
      { lower: 45000,  upper: 135000, lower_inclusive: false, upper_inclusive: true, rate: 0.30, fixed_at_lower: 4288 },
      { lower: 135000, upper: 190000, lower_inclusive: false, upper_inclusive: true, rate: 0.37, fixed_at_lower: 31288 },
      { lower: 190000, upper: null,   lower_inclusive: false, upper_inclusive: false, rate: 0.45, fixed_at_lower: 51638 },
    ],
  },

  // ── LITO — Low Income Tax Offset ──────────────────────────────────────────
  {
    rule_id: "federal/lito/2025-26",
    fy: "2025-26",
    category: "lito",
    jurisdiction: "federal",
    shape: "table",
    plain_english:
      "Low Income Tax Offset reduces tax for incomes up to $66,667. " +
      "Maximum $700 (income ≤ $37,500); phases out 5c per $1 between $37,500 " +
      "and $45,000 ($325 at $45k); then 1.5c per $1 to $66,667 (zero).",
    technical:
      "ITAA 1936 s.159N. Two-segment phase-out: 5% from $37,500 ($700 max) to " +
      "$45,000 ($325 mid-point); 1.5% from $45,000 to $66,667 ($0).",
    legislation_ref: "ITAA 1936 s.159N",
    source_url: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/tax-offsets/low-and-middle-income-tax-offset",
    source_retrieved: "2026-05-02",
    last_reviewed: "2026-05-04",
    applies_to: ["resident individuals"],
    formula_ref: "calcLito (calculations.ts)",
    table: [
      { key: "max",                    value: 700,    note: "Maximum offset at low incomes" },
      { key: "phaseOutLowerThreshold", value: 37500,  note: "Full $700 retained at or below this" },
      { key: "phaseOutLowerRate",      value: 0.05,   note: "5c per $1 above lower threshold" },
      { key: "midPointAmount",         value: 325,    note: "Offset value at $45,000" },
      { key: "phaseOutUpperThreshold", value: 45000,  note: "Phase-out rate switches here" },
      { key: "phaseOutUpperRate",      value: 0.015,  note: "1.5c per $1 above upper threshold" },
      { key: "upperBoundary",          value: 66667,  note: "Offset = 0 above this" },
    ],
  },

  // ── Medicare levy ─────────────────────────────────────────────────────────
  {
    rule_id: "federal/medicare/2025-26",
    fy: "2025-26",
    category: "medicare",
    jurisdiction: "federal",
    shape: "table",
    plain_english:
      "Medicare levy is 2% of taxable income for most residents. " +
      "Phases in for low incomes ($27,222–$34,027 single) at 10% of excess. " +
      "Half-levy (1%) applies when the taxpayer qualifies for partial exemption.",
    technical:
      "Medicare Levy Act 1986. Phase-in rate 10% on income above " +
      "$27,222 up to the $34,027 upper threshold (singles); full 2% above. " +
      "reducedRate (1%) handles the medicareReduction flag.",
    legislation_ref: "Medicare Levy Act 1986",
    source_url: "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-reduction/medicare-levy-reduction-for-low-income-earners",
    source_retrieved: "2026-05-02",
    last_reviewed: "2026-05-04",
    applies_to: ["resident individuals (excluding family-threshold cases — see B116 V1 note)"],
    excluded: ["medicare_exempt taxpayers (foreign Medicare entitlements / exemption certificates)"],
    formula_ref: "calcMedicareLevy / effectiveMarginalRate (calculations.ts)",
    table: [
      { key: "rate",                  value: 0.02,  note: "Standard rate above upper threshold" },
      { key: "reducedRate",           value: 0.01,  note: "Half-levy when medicareReduction flag set" },
      { key: "singleLowerThreshold", value: 27222, note: "Phase-in starts above this" },
      { key: "singleUpperThreshold", value: 34027, note: "Full rate above this" },
      { key: "phaseInRate",           value: 0.10,  note: "10c per $1 of excess in phase-in band" },
    ],
  },

  // ── MLS — Medicare Levy Surcharge (singles + family) ──────────────────────
  {
    rule_id: "federal/mls/2025-26",
    fy: "2025-26",
    category: "mls",
    jurisdiction: "federal",
    shape: "conditional",
    plain_english:
      "Medicare Levy Surcharge applies to high-income taxpayers without " +
      "private hospital cover. Singles tiers from $101,000 (1%) to $158,000+ " +
      "(1.5%); family tiers from $202,000 (1%) to $316,000+ (1.5%) with a " +
      "$1,500 uplift per dependent child after the first.",
    technical:
      "Medicare Levy Act 1986 Part VIIB / IT(TR)A 1936 s.8B–8G. Tiered MLS " +
      "with separate singles + family tier tables. Family path applies when " +
      "lens has a partner; uplift = $1,500 × max(0, dependentChildren − 1).",
    legislation_ref: "Medicare Levy Act 1986 Part VIIB",
    source_url: "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates",
    source_retrieved: "2026-05-02",
    last_reviewed: "2026-05-04",
    applies_to: ["resident individuals without complying private hospital cover, above thresholds"],
    excluded: [
      "medicare_exempt (no MLS)",
      "private hospital cover (no MLS)",
      "family income at/below base threshold",
    ],
    formula_ref: "mlsRateForIncome / effectiveMarginalRate (calculations.ts)",
    metadata: {
      dependentChildUplift: 1500,
    },
    conditions: [
      {
        when: "singles",
        then: [
          { upper: 101000,   rate: 0      },
          { upper: 118000,   rate: 0.01   },
          { upper: 158000,   rate: 0.0125 },
          { upper: null,     rate: 0.015  },
        ],
        note: "Income evaluated against own taxable income.",
      },
      {
        when: "family",
        then: [
          { upper: 202000,   rate: 0      },
          { upper: 236000,   rate: 0.01   },
          { upper: 316000,   rate: 0.0125 },
          { upper: null,     rate: 0.015  },
        ],
        note: "Combined family income; thresholds uplifted $1,500 per dependent child after the first.",
      },
    ],
  },

  // ── HELP / HECS — 2025-26 marginal-rate schedule ──────────────────────────
  {
    rule_id: "federal/help/2025-26",
    fy: "2025-26",
    category: "help",
    jurisdiction: "federal",
    shape: "brackets",
    plain_english:
      "Compulsory study-loan repayment for FY 2025-26. Nothing below $67,000. " +
      "$67,001–$125,000: 15c per dollar above $67,000. $125,001–$179,285: " +
      "$8,700 plus 17c per dollar above $125,000. $179,286+: flat 10% × full income.",
    technical:
      "Higher Education Support Act 2003 + Income Tax Assessment Act. " +
      "**Schedule SHAPE changed in FY 2025-26** from 18-tier single-rate-on-" +
      "full-income to 3-tier marginal-on-excess (with flat top tier). See L43 + " +
      "B118 audit. fullIncomeRate on the top bracket flips to flat-rate × full income.",
    legislation_ref: "Higher Education Support Act 2003 + ITAA 1997",
    source_url: "https://www.ato.gov.au/tax-rates-and-codes/study-and-training-support-loans-rates-and-repayment-thresholds",
    source_retrieved: "2026-05-02",
    last_reviewed: "2026-05-04",
    applies_to: ["taxpayers with HELP / VSL / SFSS / Trade Support Loans"],
    excluded: ["taxpayers with no study debt"],
    formula_ref: "helpRepaymentAmount / helpMarginalRate (calculations.ts)",
    metadata: {
      minIncomeThreshold: 67000,
    },
    brackets: [
      { lower: 0,      upper: 67000,  lower_inclusive: true,  upper_inclusive: true,  rate: 0 },
      { lower: 67000,  upper: 125000, lower_inclusive: false, upper_inclusive: true,  rate: 0.15, fixed_at_lower: 0 },
      { lower: 125000, upper: 179285, lower_inclusive: false, upper_inclusive: true,  rate: 0.17, fixed_at_lower: 8700 },
      { lower: 179286, upper: null,   lower_inclusive: true,  upper_inclusive: false, rate: 0,    fullIncomeRate: 0.10 },
    ],
  },
];

registerFY("2025-26", RULES_2025_26);
